import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Send, CheckCircle, CreditCard, XCircle, Loader2, Mail, Eye } from "lucide-react";
import { isOnRequestBooking } from "@ckgo2/shared/utils";
import ConfirmModal from "../common/ConfirmModal";

interface OfferStatus {
  sent: boolean;
  offerSentAt: string | null;
  viewed: boolean;
  lastViewedAt: string | null;
  viewCount: number;
}

const fmtDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";

interface StatusActionsProps {
  orderId: string;
  currentStatus: string;
  totalPrice: number;
  depositAmount: number;
  paidAmount?: number;
  // Capacity-warning data: only relevant for FIXED-departure bookings (shared capacity).
  tripKind?: "tour" | "experience";
  fulfillmentMode?: "fixed_departures" | "on_request";
  pax?: number;
  tripDateCapacity?: number;
  tripDateBookedCount?: number;
  onStatusChange?: () => void;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, note: string) => void;
  isLoading: boolean;
  suggestedAmount: number;
  title: string;
}

function PaymentModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  suggestedAmount,
  title,
}: PaymentModalProps) {
  const [amount, setAmount] = useState(suggestedAmount);
  const [note, setNote] = useState("");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Amount (USD)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="input"
              min={0}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input resize-none"
              rows={2}
              placeholder="E.g. reference number, payment date..."
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost" disabled={isLoading}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(amount, note)}
            className="btn-primary"
            disabled={isLoading || amount <= 0}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            Record payment
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StatusActions({
  orderId,
  currentStatus,
  totalPrice,
  depositAmount,
  paidAmount = 0,
  tripKind,
  fulfillmentMode,
  pax,
  tripDateCapacity,
  tripDateBookedCount,
  onStatusChange,
}: StatusActionsProps) {
  const tryOpenConfirm = (action: string, newStatus: string) => {
    if (
      newStatus === "confirmed" &&
      !isOnRequestBooking({ fulfillmentMode, tripKind }) &&
      typeof tripDateCapacity === "number" &&
      typeof tripDateBookedCount === "number" &&
      typeof pax === "number"
    ) {
      const wouldBe = tripDateBookedCount + pax;
      if (wouldBe > tripDateCapacity) {
        setShowConfirmModal({ action: "exceed-capacity", newStatus });
        return;
      }
    }
    setShowConfirmModal({ action, newStatus });
  };
  const queryClient = useQueryClient();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<{
    action: string;
    newStatus: string;
  } | null>(null);
  const [paymentType, setPaymentType] = useState<"deposit" | "full">("deposit");

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      return api.patch(`/api/admin/orders/${orderId}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setShowConfirmModal(null);
      onStatusChange?.();
    },
  });

  const recordPaymentMutation = useMutation({
    mutationFn: async ({ amount, note }: { amount: number; note: string }) => {
      return api.post(`/api/admin/orders/${orderId}/payments`, { amount, note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setShowPaymentModal(false);
      onStatusChange?.();
    },
  });

  // Has the offer been sent / viewed? (Drives the "Send offer" → "Resend offer" label + the status line.)
  const { data: offerRes } = useQuery({
    queryKey: ["offer-status", orderId],
    queryFn: () => api.get<{ data: OfferStatus }>(`/api/admin/orders/${orderId}/offer-status`),
  });
  const offer = offerRes?.data;

  // The unified Send offer: one real action (mint link + render template + email + status + tracking).
  const sendOfferMutation = useMutation({
    mutationFn: async () => api.post(`/api/admin/orders/${orderId}/send-offer`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["offer-status", orderId] });
      setShowConfirmModal(null);
      onStatusChange?.();
    },
  });

  // Outstanding balance = total minus what has actually been received (not the deposit
  // target), so it stays correct after item edits or part payments.
  const remainingAmount = Math.max(0, totalPrice - paidAmount);
  // Deposit suggestion = the order's deposit (kept at the configured % by the backend).
  const suggestedDeposit = depositAmount || 0;

  const handleStatusChange = (newStatus: string) => {
    updateStatusMutation.mutate(newStatus);
  };

  const handlePayment = (amount: number, note: string) => {
    recordPaymentMutation.mutate({ amount, note });
  };

  const renderActions = () => {
    switch (currentStatus) {
      case "inquiry":
        return (
          <button
            onClick={() =>
              setShowConfirmModal({
                action: "sendOffer",
                newStatus: "quoted",
              })
            }
            className="btn-primary w-full"
          >
            <Send className="h-5 w-5" />
            Send offer
          </button>
        );

      case "quoted":
        return (
          <>
            <button
              onClick={() => tryOpenConfirm("confirm", "confirmed")}
              className="btn-primary w-full"
            >
              <CheckCircle className="h-5 w-5" />
              Confirm order
            </button>
            <button
              onClick={() => setShowConfirmModal({ action: "sendOffer", newStatus: "quoted" })}
              className="btn-ghost w-full"
            >
              <Send className="h-4 w-4" />
              Resend offer
            </button>
          </>
        );

      case "confirmed":
        return (
          <button
            onClick={() => {
              setPaymentType("deposit");
              setShowPaymentModal(true);
            }}
            className="btn-primary w-full"
          >
            <CreditCard className="h-5 w-5" />
            Record deposit
          </button>
        );

      case "deposit_paid":
        return (
          <button
            onClick={() => {
              setPaymentType("full");
              setShowPaymentModal(true);
            }}
            className="btn-primary w-full"
          >
            <CreditCard className="h-5 w-5" />
            Record balance payment
          </button>
        );

      case "fully_paid":
        return (
          <button
            onClick={() =>
              setShowConfirmModal({
                action: "complete",
                newStatus: "completed",
              })
            }
            className="btn-primary w-full"
          >
            <CheckCircle className="h-5 w-5" />
            Mark as completed
          </button>
        );

      default:
        return null;
    }
  };

  const getConfirmModalProps = () => {
    switch (showConfirmModal?.action) {
      case "sendOffer":
        return {
          title: offer?.sent ? "Resend offer" : "Send offer",
          message:
            "A personalised offer email with an online quote link will be sent to the customer, and the order will move to Quoted. Continue?",
          confirmText: offer?.sent ? "Resend offer" : "Send offer",
        };
      case "confirm":
        return {
          title: "Confirm order",
          message:
            "The order will be confirmed and an email with payment instructions will be sent to the customer.",
          confirmText: "Confirm",
        };
      case "complete":
        return {
          title: "Mark as completed",
          message: "The order will be marked as completed.",
          confirmText: "Complete",
        };
      case "cancel":
        return {
          title: "Cancel order",
          message: "Are you sure you want to cancel this order? This action cannot be undone.",
          confirmText: "Cancel order",
          variant: "danger" as const,
        };
      case "exceed-capacity": {
        const wouldBe = (tripDateBookedCount ?? 0) + (pax ?? 0);
        return {
          title: "Capacity will be exceeded",
          message: `This will exceed capacity (${tripDateBookedCount}/${tripDateCapacity} booked, +${pax} new = ${wouldBe}). Confirm anyway?`,
          confirmText: "Confirm anyway",
          variant: "danger" as const,
        };
      }
      default:
        return {
          title: "",
          message: "",
          confirmText: "",
        };
    }
  };

  return (
    <div className="space-y-2">
      {renderActions()}

      {offer?.sent && (
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Mail className="h-3.5 w-3.5" />
          Offer sent {fmtDate(offer.offerSentAt)}
          {offer.viewed ? (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <Eye className="h-3.5 w-3.5" /> viewed {fmtDate(offer.lastViewedAt)}
            </span>
          ) : (
            <span>· not viewed yet</span>
          )}
        </p>
      )}

      {sendOfferMutation.isError && (
        <p className="text-error-600 dark:text-error-400 text-xs">
          {(sendOfferMutation.error as Error)?.message || "Could not send the offer"}
        </p>
      )}

      {currentStatus !== "cancelled" && currentStatus !== "completed" && (
        <button
          onClick={() =>
            setShowConfirmModal({
              action: "cancel",
              newStatus: "cancelled",
            })
          }
          className="btn-ghost text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 w-full"
        >
          <XCircle className="h-5 w-5" />
          Cancel order
        </button>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePayment}
        isLoading={recordPaymentMutation.isPending}
        suggestedAmount={paymentType === "deposit" ? suggestedDeposit : remainingAmount}
        title={paymentType === "deposit" ? "Record deposit" : "Record balance payment"}
      />

      {/* Confirm Modal */}
      {showConfirmModal && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setShowConfirmModal(null)}
          onConfirm={() =>
            showConfirmModal.action === "sendOffer"
              ? sendOfferMutation.mutate()
              : handleStatusChange(showConfirmModal.newStatus)
          }
          isLoading={updateStatusMutation.isPending || sendOfferMutation.isPending}
          {...getConfirmModalProps()}
        />
      )}
    </div>
  );
}
